import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { superAdminGuide } from '@/data/documentation/superAdminGuide';
import { rhPayeGuide } from '@/data/documentation/rhPayeGuide';
import { superviseurGuide } from '@/data/documentation/superviseurGuide';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import {
  Search, BookOpen, Shield, Users, HardHat, ChevronRight, ChevronDown,
  Lightbulb, AlertTriangle, CheckCircle, ArrowLeft, ExternalLink,
  BookMarked, Menu, X, FileText, Info, AlertCircle, ChevronUp,
  Printer, Home, Download, FileDown, FileType
} from 'lucide-react';
import { generateGuidePDF } from '@/utils/generatePDF';
import { generateGuideDocx } from '@/utils/generateDocx';

const GUIDE_MAP = {
  'super-admin': superAdminGuide,
  'rh-paie': rhPayeGuide,
  'superviseur': superviseurGuide,
};

const GUIDE_ICONS = {
  'super-admin': Shield,
  'rh-paie': Users,
  'superviseur': HardHat,
};

const GUIDE_COLORS = {
  'super-admin': { bg: 'bg-purple-50 dark:bg-purple-950/30', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-800', accent: 'bg-purple-600' },
  'rh-paie': { bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-200 dark:border-blue-800', accent: 'bg-blue-600' },
  'superviseur': { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-800', accent: 'bg-emerald-600' },
};

/* ===== Contextual Help Button (exported for use on other pages) ===== */
export function HelpButton({ guide, section, subsection, className = '' }) {
  return (
    <a
      href={`/admin/documentation?guide=${guide}&section=${section}${subsection ? `&sub=${subsection}` : ''}`}
      className={`inline-flex items-center gap-1 text-xs text-slate-400 hover:text-primary transition-colors ${className}`}
      title="Aide"
      data-testid="contextual-help-btn"
    >
      <BookOpen size={14} />
      <span>Aide</span>
    </a>
  );
}

/* ===== Step Renderer ===== */
function StepList({ steps }) {
  if (!steps?.length) return null;
  return (
    <div className="space-y-3 my-4" data-testid="doc-steps">
      {steps.map((step, i) => (
        <div key={i} className="flex gap-3">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
            <span className="text-xs font-bold text-primary">{i + 1}</span>
          </div>
          <div className="flex-1 pt-0.5">
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{step}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ===== Alert Box ===== */
function AlertBox({ type, children }) {
  const config = {
    tip: { icon: Lightbulb, bg: 'bg-blue-50 dark:bg-blue-950/40', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-800 dark:text-blue-200', iconColor: 'text-blue-500' },
    warning: { icon: AlertTriangle, bg: 'bg-amber-50 dark:bg-amber-950/40', border: 'border-amber-200 dark:border-amber-800', text: 'text-amber-800 dark:text-amber-200', iconColor: 'text-amber-500' },
    success: { icon: CheckCircle, bg: 'bg-emerald-50 dark:bg-emerald-950/40', border: 'border-emerald-200 dark:border-emerald-800', text: 'text-emerald-800 dark:text-emerald-200', iconColor: 'text-emerald-500' },
    error: { icon: AlertCircle, bg: 'bg-red-50 dark:bg-red-950/40', border: 'border-red-200 dark:border-red-800', text: 'text-red-800 dark:text-red-200', iconColor: 'text-red-500' },
    info: { icon: Info, bg: 'bg-slate-50 dark:bg-slate-800/60', border: 'border-slate-200 dark:border-slate-700', text: 'text-slate-700 dark:text-slate-300', iconColor: 'text-slate-500' },
  };
  const c = config[type] || config.info;
  const Icon = c.icon;
  return (
    <div className={`flex gap-3 p-3.5 rounded-lg border ${c.bg} ${c.border} ${c.text} my-3`}>
      <Icon size={18} className={`flex-shrink-0 mt-0.5 ${c.iconColor}`} />
      <div className="text-sm leading-relaxed flex-1">{children}</div>
    </div>
  );
}

/* ===== Error Table ===== */
function ErrorTable({ errors }) {
  if (!errors?.length) return null;
  return (
    <div className="my-4 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="doc-errors">
      <div className="bg-red-50 dark:bg-red-950/30 px-4 py-2 border-b border-slate-200 dark:border-slate-700">
        <h4 className="text-sm font-semibold text-red-700 dark:text-red-300 flex items-center gap-2">
          <AlertCircle size={14} /> Erreurs frequentes
        </h4>
      </div>
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {errors.map((err, i) => (
          <div key={i} className="p-4 grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <span className="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wide">Probleme</span>
              <p className="text-sm text-slate-700 dark:text-slate-300 mt-0.5">{err.problem}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">Solution</span>
              <p className="text-sm text-slate-700 dark:text-slate-300 mt-0.5">{err.solution}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ===== Screenshot Component ===== */
function Screenshot({ src, alt, annotations }) {
  if (!src) return null;
  return (
    <div className="my-5 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm" data-testid="doc-screenshot">
      <div className="bg-slate-100 dark:bg-slate-800 px-3 py-1.5 flex items-center gap-2 border-b border-slate-200 dark:border-slate-700">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        </div>
        <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-2 font-mono">{alt || 'PunchTM'}</span>
      </div>
      <div className="relative bg-white dark:bg-slate-900">
        <img src={src} alt={alt || ''} className="w-full h-auto" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
        {annotations?.map((ann, i) => (
          <div
            key={i}
            className="absolute w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center shadow-lg ring-2 ring-white dark:ring-slate-900 cursor-help"
            style={{ top: ann.top, left: ann.left }}
            title={ann.label}
          >
            {i + 1}
          </div>
        ))}
      </div>
      {annotations?.length > 0 && (
        <div className="bg-slate-50 dark:bg-slate-800/50 px-4 py-3 border-t border-slate-200 dark:border-slate-700">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {annotations.map((ann, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400">
                <span className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center flex-shrink-0 text-[10px] font-bold">{i + 1}</span>
                <span>{ann.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ===== Subsection Content Renderer ===== */
function SubsectionContent({ sub, guideColor }) {
  return (
    <div className="space-y-5" data-testid={`doc-sub-${sub.id}`}>
      {/* Description */}
      <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">{sub.description}</p>

      {/* When to use */}
      {sub.whenToUse && (
        <AlertBox type="info">{sub.whenToUse}</AlertBox>
      )}

      {/* Steps */}
      {sub.steps && (
        <div>
          <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2 flex items-center gap-2">
            <FileText size={14} className="text-primary" /> Etapes
          </h4>
          <StepList steps={sub.steps} />
        </div>
      )}

      {/* Screenshot */}
      <Screenshot src={sub.screenshot} alt={sub.screenshotAlt} annotations={sub.annotations} />

      {/* Expected Result */}
      {sub.result && (
        <AlertBox type="success">{sub.result}</AlertBox>
      )}

      {/* Tips */}
      {sub.tips?.map((tip, i) => (
        <AlertBox key={i} type="tip">{tip}</AlertBox>
      ))}

      {/* Warnings */}
      {sub.warnings?.map((w, i) => (
        <AlertBox key={i} type="warning">{w}</AlertBox>
      ))}

      {/* Errors */}
      <ErrorTable errors={sub.errors} />
    </div>
  );
}

/* ===== MAIN DOCUMENTATION PAGE ===== */
export default function DocumentationPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const contentRef = useRef(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // State from URL params
  const [activeGuideId, setActiveGuideId] = useState(searchParams.get('guide') || 'rh-paie');
  const [activeSectionId, setActiveSectionId] = useState(searchParams.get('section') || null);
  const [activeSubId, setActiveSubId] = useState(searchParams.get('sub') || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({});

  const guide = GUIDE_MAP[activeGuideId] || rhPayeGuide;
  const guideColor = GUIDE_COLORS[activeGuideId] || GUIDE_COLORS['rh-paie'];

  // Initialize first section if none selected
  useEffect(() => {
    if (!activeSectionId && guide.sections.length > 0) {
      setActiveSectionId(guide.sections[0].id);
      setExpandedSections({ [guide.sections[0].id]: true });
      if (guide.sections[0].content?.length > 0) {
        setActiveSubId(guide.sections[0].content[0].id);
      }
    }
  }, [activeGuideId]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('guide', activeGuideId);
    if (activeSectionId) params.set('section', activeSectionId);
    if (activeSubId) params.set('sub', activeSubId);
    setSearchParams(params, { replace: true });
  }, [activeGuideId, activeSectionId, activeSubId]);

  // Scroll to top on section change
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSectionId, activeSubId]);

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    const results = [];
    Object.values(GUIDE_MAP).forEach(g => {
      g.sections.forEach(s => {
        s.content?.forEach(sub => {
          const text = `${sub.title} ${sub.description} ${sub.steps?.join(' ') || ''} ${sub.whenToUse || ''}`.toLowerCase();
          if (text.includes(q)) {
            results.push({ guideId: g.id, guideTitle: g.title, section: s, sub });
          }
        });
      });
    });
    return results;
  }, [searchQuery]);

  // Navigation helpers
  const selectGuide = (id) => {
    setActiveGuideId(id);
    setActiveSectionId(null);
    setActiveSubId(null);
    setExpandedSections({});
    setSearchQuery('');
    setMobileSidebarOpen(false);
  };

  const selectSection = (sectionId) => {
    setActiveSectionId(sectionId);
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
    const section = guide.sections.find(s => s.id === sectionId);
    if (section?.content?.length > 0) {
      setActiveSubId(section.content[0].id);
    }
  };

  const selectSubsection = (sectionId, subId) => {
    setActiveSectionId(sectionId);
    setActiveSubId(subId);
    setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
    setMobileSidebarOpen(false);
  };

  const selectSearchResult = (result) => {
    setActiveGuideId(result.guideId);
    setActiveSectionId(result.section.id);
    setActiveSubId(result.sub.id);
    setExpandedSections({ [result.section.id]: true });
    setSearchQuery('');
    setMobileSidebarOpen(false);
  };

  // Current content
  const currentSection = guide.sections.find(s => s.id === activeSectionId);
  const currentSub = currentSection?.content?.find(c => c.id === activeSubId);

  // Available guides based on role
  const availableGuides = useMemo(() => {
    const role = user?.role;
    if (role === 'platform_superadmin') return ['super-admin', 'rh-paie', 'superviseur'];
    if (['tenant_superuser', 'tenant_hr', 'tenant_admin', 'admin'].includes(role)) return ['rh-paie', 'superviseur'];
    return ['superviseur'];
  }, [user]);

  const handlePrint = () => window.print();

  const [downloading, setDownloading] = useState(null);

  const handleDownloadPDF = async () => {
    setDownloading('pdf');
    try {
      generateGuidePDF(guide);
    } catch (e) {
      console.error('PDF generation error:', e);
    }
    setDownloading(null);
  };

  const handleDownloadDocx = async () => {
    setDownloading('docx');
    try {
      await generateGuideDocx(guide);
    } catch (e) {
      console.error('DOCX generation error:', e);
    }
    setDownloading(null);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] lg:h-[calc(100vh-2rem)] -m-6 lg:-m-8" data-testid="documentation-page">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            className="lg:hidden p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
            data-testid="doc-mobile-menu"
          >
            {mobileSidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <BookMarked size={22} className="text-primary hidden sm:block" />
          <div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-white" data-testid="doc-title">Documentation</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 hidden sm:block">Guide complet de PunchTM</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadPDF}
            disabled={downloading === 'pdf'}
            className="hidden sm:flex gap-1.5"
            data-testid="doc-download-pdf"
          >
            <FileDown size={14} />
            {downloading === 'pdf' ? 'Export...' : 'PDF'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadDocx}
            disabled={downloading === 'docx'}
            className="hidden sm:flex gap-1.5"
            data-testid="doc-download-docx"
          >
            <FileType size={14} />
            {downloading === 'docx' ? 'Export...' : 'Word'}
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} className="hidden sm:flex gap-1.5" data-testid="doc-print-btn">
            <Printer size={14} /> Imprimer
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* ===== SIDEBAR ===== */}
        <aside className={`
          w-72 flex-shrink-0 border-r border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50
          flex flex-col overflow-hidden
          fixed lg:relative inset-y-0 left-0 z-30 lg:z-0
          transform transition-transform duration-200
          ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `} data-testid="doc-sidebar">
          {/* Guide Selector */}
          <div className="p-3 space-y-2 border-b border-slate-200 dark:border-slate-800">
            {availableGuides.map(gId => {
              const g = GUIDE_MAP[gId];
              const Icon = GUIDE_ICONS[gId];
              const colors = GUIDE_COLORS[gId];
              const isActive = activeGuideId === gId;
              return (
                <button
                  key={gId}
                  onClick={() => selectGuide(gId)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm font-medium transition-all
                    ${isActive
                      ? `${colors.bg} ${colors.text} ${colors.border} border`
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent'
                    }`}
                  data-testid={`doc-guide-${gId}`}
                >
                  <Icon size={16} />
                  <span className="truncate">{g.title}</span>
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="p-3 border-b border-slate-200 dark:border-slate-800">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Rechercher..."
                className="pl-8 h-8 text-sm bg-white dark:bg-slate-800"
                data-testid="doc-search"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Search Results */}
          {searchResults !== null ? (
            <ScrollArea className="flex-1">
              <div className="p-3 space-y-1">
                {searchResults.length === 0 ? (
                  <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-8">Aucun resultat</p>
                ) : (
                  <>
                    <p className="text-xs text-slate-500 mb-2 px-1">{searchResults.length} resultat(s)</p>
                    {searchResults.map((r, i) => (
                      <button
                        key={i}
                        onClick={() => selectSearchResult(r)}
                        className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        data-testid={`doc-search-result-${i}`}
                      >
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">{r.sub.title}</p>
                        <p className="text-xs text-slate-400 truncate">{r.guideTitle} &gt; {r.section.title}</p>
                      </button>
                    ))}
                  </>
                )}
              </div>
            </ScrollArea>
          ) : (
            /* Section Navigation Tree */
            <ScrollArea className="flex-1">
              <nav className="p-3 space-y-0.5" data-testid="doc-nav">
                {guide.sections.map(section => {
                  const isExpanded = expandedSections[section.id];
                  const isActiveSection = activeSectionId === section.id;
                  return (
                    <div key={section.id}>
                      <button
                        onClick={() => selectSection(section.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-all
                          ${isActiveSection
                            ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-semibold shadow-sm'
                            : 'text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800/60 font-medium'
                          }`}
                        data-testid={`doc-section-${section.id}`}
                      >
                        {isExpanded ? <ChevronDown size={14} className="flex-shrink-0" /> : <ChevronRight size={14} className="flex-shrink-0" />}
                        <span className="truncate">{section.title}</span>
                        {section.content && (
                          <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">{section.content.length}</Badge>
                        )}
                      </button>
                      {isExpanded && section.content && (
                        <div className="ml-5 mt-0.5 space-y-0.5 border-l-2 border-slate-200 dark:border-slate-700 pl-2">
                          {section.content.map(sub => (
                            <button
                              key={sub.id}
                              onClick={() => selectSubsection(section.id, sub.id)}
                              className={`w-full text-left px-2.5 py-1.5 rounded-md text-xs transition-all
                                ${activeSubId === sub.id
                                  ? `${guideColor.bg} ${guideColor.text} font-semibold`
                                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/40'
                                }`}
                              data-testid={`doc-sub-nav-${sub.id}`}
                            >
                              {sub.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </ScrollArea>
          )}
        </aside>

        {/* Mobile overlay */}
        {mobileSidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-20 bg-black/40" onClick={() => setMobileSidebarOpen(false)} />
        )}

        {/* ===== CONTENT AREA ===== */}
        <div className="flex-1 overflow-hidden flex flex-col" ref={contentRef}>
          <ScrollArea className="flex-1">
            <div className="max-w-4xl mx-auto px-5 sm:px-8 py-6 sm:py-8">
              {/* Welcome / No selection */}
              {!currentSection ? (
                <div className="text-center py-16" data-testid="doc-welcome">
                  <div className={`w-16 h-16 rounded-2xl ${guideColor.accent} flex items-center justify-center mx-auto mb-5`}>
                    <BookOpen size={28} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{guide.title}</h2>
                  <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-8">{guide.subtitle}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-2xl mx-auto">
                    {guide.sections.map(s => (
                      <button
                        key={s.id}
                        onClick={() => selectSection(s.id)}
                        className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 hover:shadow-md hover:border-primary/30 transition-all text-left group"
                        data-testid={`doc-section-card-${s.id}`}
                      >
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-white group-hover:text-primary transition-colors">{s.title}</h3>
                        <p className="text-xs text-slate-400 mt-1 line-clamp-2">{s.overview}</p>
                        <span className="text-xs text-primary mt-2 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          Voir <ChevronRight size={12} />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {/* Breadcrumb */}
                  <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500 mb-6" data-testid="doc-breadcrumb">
                    <button onClick={() => { setActiveSectionId(null); setActiveSubId(null); }} className="hover:text-primary transition-colors flex items-center gap-1">
                      <Home size={12} /> {guide.title}
                    </button>
                    <ChevronRight size={12} />
                    <span className="text-slate-600 dark:text-slate-300 font-medium">{currentSection.title}</span>
                    {currentSub && (
                      <>
                        <ChevronRight size={12} />
                        <span className="text-slate-600 dark:text-slate-300 font-medium truncate">{currentSub.title}</span>
                      </>
                    )}
                  </div>

                  {/* Section Header */}
                  <div className="mb-8">
                    <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2" data-testid="doc-section-title">
                      {currentSection.title}
                    </h2>
                    {currentSection.overview && (
                      <p className="text-base text-slate-500 dark:text-slate-400 leading-relaxed">{currentSection.overview}</p>
                    )}
                  </div>

                  {/* Subsection Navigation (horizontal) */}
                  {currentSection.content?.length > 1 && (
                    <div className="flex flex-wrap gap-1.5 mb-8 pb-4 border-b border-slate-200 dark:border-slate-700" data-testid="doc-sub-tabs">
                      {currentSection.content.map(sub => (
                        <button
                          key={sub.id}
                          onClick={() => setActiveSubId(sub.id)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all
                            ${activeSubId === sub.id
                              ? `${guideColor.accent} text-white shadow-sm`
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                            }`}
                          data-testid={`doc-sub-tab-${sub.id}`}
                        >
                          {sub.title}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Active Subsection Content */}
                  {currentSub && (
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4" data-testid="doc-subsection-title">
                        {currentSub.title}
                      </h3>
                      <SubsectionContent sub={currentSub} guideColor={guideColor} />
                    </div>
                  )}

                  {/* Navigation prev/next */}
                  <Separator className="my-8" />
                  <NavigationFooter
                    guide={guide}
                    currentSectionId={activeSectionId}
                    currentSubId={activeSubId}
                    onNavigate={(sId, subId) => { setActiveSectionId(sId); setActiveSubId(subId); setExpandedSections(prev => ({ ...prev, [sId]: true })); }}
                  />
                </>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}

/* ===== Prev/Next Footer Navigation ===== */
function NavigationFooter({ guide, currentSectionId, currentSubId, onNavigate }) {
  const allItems = [];
  guide.sections.forEach(s => {
    s.content?.forEach(sub => {
      allItems.push({ sectionId: s.id, sectionTitle: s.title, subId: sub.id, subTitle: sub.title });
    });
  });

  const currentIndex = allItems.findIndex(i => i.sectionId === currentSectionId && i.subId === currentSubId);
  const prev = currentIndex > 0 ? allItems[currentIndex - 1] : null;
  const next = currentIndex < allItems.length - 1 ? allItems[currentIndex + 1] : null;

  return (
    <div className="flex justify-between gap-4 pb-8" data-testid="doc-navigation-footer">
      {prev ? (
        <button
          onClick={() => onNavigate(prev.sectionId, prev.subId)}
          className="flex-1 p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-primary/30 hover:shadow-sm transition-all text-left group"
          data-testid="doc-nav-prev"
        >
          <span className="text-xs text-slate-400 flex items-center gap-1"><ArrowLeft size={12} /> Precedent</span>
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 group-hover:text-primary transition-colors mt-1 truncate">{prev.subTitle}</p>
          <p className="text-xs text-slate-400 truncate">{prev.sectionTitle}</p>
        </button>
      ) : <div className="flex-1" />}
      {next ? (
        <button
          onClick={() => onNavigate(next.sectionId, next.subId)}
          className="flex-1 p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-primary/30 hover:shadow-sm transition-all text-right group"
          data-testid="doc-nav-next"
        >
          <span className="text-xs text-slate-400 flex items-center gap-1 justify-end">Suivant <ChevronRight size={12} /></span>
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 group-hover:text-primary transition-colors mt-1 truncate">{next.subTitle}</p>
          <p className="text-xs text-slate-400 truncate">{next.sectionTitle}</p>
        </button>
      ) : <div className="flex-1" />}
    </div>
  );
}
